import React, { useEffect, useState, useRef } from "react"
import * as Sentry from "@sentry/react"
import { useAtom } from "jotai"
import { useNavigate } from "react-router-dom"
import { format, parseISO, differenceInSeconds } from "date-fns"
import { isEmpty } from "lodash"

import transpais from "../../services/transpais"
import { useModalView } from "../../contexts/modalview.context"
import { useLogger } from "../../contexts/logger.context"
import { useTimer } from "../../contexts/timer.context"
import { startBookingTimer } from "../../helpers/bookingTimer"
import { TripType } from "../../components/transpais/enums"
import { BASE_URL } from "../../utilities/constants"

import {
  claveUsuario,
  usuarioID,
  puntoVentaID,
  estacionID,
  accessToken,
  nombrePuntoVenta,
  bookingSessionOrigin,
  bookingSessionDestination,
  bookingSessionDepartureTrip,
  bookingSessionDepartureDisponibilidad,
  bookingSessionDepartureSelectedTickets,
  bookingSessionDepartureBlockedTickets,
  bookingSessionDepartureDate,
  bookingSessionReturningDate,
  bookingSessionTripConfig,
  bookingSessionReturningTrip,
  bookingSessionReturningBlockedTickets,
  bookingSessionDepartureTripId,
  bookingSessionReturningTripId,
  usuarioEmpresa,
  TranspaisIDToken,
  bookingSessionTrips,
  bookingSessionCountFares,
  bookingSessionFares,
  bookingSessionPassengers,
  bookingSessionGlobalTrips,
  bookingSessionTimeoutDeadline,
  settingsConfig,
} from "../../utilities/atoms"

import {
  isSingleTrip,
  isRoundTripDeparture,
  isRoundTripReturn,
  isSelectingRoundTripDeparture,
  isSelectingRoundTripReturn,
} from "../../helpers/tripHelpers"

import styles from "./BookingTrips.module.scss"
import global from "./BookingGlobal.module.scss"
import {
  Button,
  Carrousel,
  Departure,
  Timer,
  TripData,
  Loader,
} from "../../components/transpais"
import { DepartureViewVariant } from "../../components/transpais/enums"
import { filterFares } from "../../helpers/tripHelpers"
import { useModal } from "../../contexts/modal.context"
import { handleBookingTimeout } from "../../helpers/bookingTimer"

// Hooks personalizados
const useTripData = () => {
  const [globalTrips, setGlobalTrips] = useAtom(bookingSessionGlobalTrips)
  const [trips, setTrips] = useState([])
  const [tripPages, setTripPages] = useState([])
  const [localLoading, setLocalLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [session_id, setSessionId] = useAtom(bookingSessionTrips)
  const [passengers, setPassengers] = useAtom(bookingSessionPassengers)

  const searchForTrips = async (
    origin,
    destination,
    departureDate,
    returnDate,
    isRoundTrip,
    bookingFares,
  ) => {
    setLocalLoading(true)
    setNotFound(false)

    try {
      const formattedDepartureDate = format(
        parseISO(departureDate),
        "yyyy-MM-dd",
      ) // Formato correcto
      let formattedReturnDate = ""
      if (isRoundTrip) {
        formattedReturnDate = returnDate
          ? format(parseISO(returnDate), "yyyy-MM-dd")
          : null // Formato correcto
      }
      const response = await transpais.search(
        origin._id,
        origin.name,
        origin.shortcode,
        destination._id,
        destination.name,
        destination.shortcode,
        formattedDepartureDate,
        formattedReturnDate,
        bookingFares,
      )
      console.log("response", response)
      setGlobalTrips(response.data.trips)
      setSessionId(response.data.session_id)
      const departures = response.data.trips.departures
      setTrips(departures)
      console.log("Viajes encontrados:", departures)

      if (departures.length === 0) {
        setNotFound(true)
      } else {
        setNotFound(false)
      }
    } catch (error) {
      console.error("Error buscando viajes:", error)
      Sentry.captureException(error)
      setNotFound(true)
    } finally {
      setLocalLoading(false)
    }
  }

  const organizeTripsIntoPages = (trips, itemsPerPage = 6) => {
    const pages = []
    for (let i = 0; i < trips.length; i += itemsPerPage) {
      pages.push(trips.slice(i, i + itemsPerPage))
    }
    setTripPages(pages)
  }

  return {
    trips,
    tripPages,
    localLoading,
    passengers,
    globalTrips,
    setLocalLoading,
    notFound,
    searchForTrips,
    setTrips,
    organizeTripsIntoPages,
  }
}

// Componente principal
const BookingTrips = () => {
  const navigate = useNavigate()
  const { loggerDebug } = useLogger()
  const { hideTimer, showTimer, setupTimer } = useTimer()
  const { showModal, hideModal, setupModal } = useModal()

  const [bookingOrigin] = useAtom(bookingSessionOrigin)
  const [bookingDestination] = useAtom(bookingSessionDestination)
  const [bookingDepartureDate] = useAtom(bookingSessionDepartureDate)
  const [bookingReturningDate] = useAtom(bookingSessionReturningDate)
  const [bookingTripConfig, setBookingTripConfig] = useAtom(
    bookingSessionTripConfig,
  )
  const [bookingDepartureTrip, setBookingDepartureTrip] = useAtom(
    bookingSessionDepartureTrip,
  )
  const [bookingReturningTrip, setBookingReturningTrip] = useAtom(
    bookingSessionReturningTrip,
  )
  const [bookingDepartureTripId, setBookingDepartureTripId] = useAtom(
    bookingSessionDepartureTripId,
  )
  const [bookingReturningTripId, setBookingReturningTripId] = useAtom(
    bookingSessionReturningTripId,
  )
  const [bookingDepartureBlockedTickets, setBookingDepartureBlockedTickets] =
    useAtom(bookingSessionDepartureBlockedTickets)
  const [bookingReturningBlockedTickets, setBookingReturningBlockedTickets] =
    useAtom(bookingSessionReturningBlockedTickets)
  const [bookingFares] = useAtom(bookingSessionCountFares)
  const [transpaisIdToken] = useAtom(TranspaisIDToken)
  const [toUpdateFareList, setToUpdateFareList] = useAtom(bookingSessionFares)
  const [shouldRedirectToBus, setShouldRedirectToBus] = useState(false)
  const [selected, setSelected] = useAtom(bookingSessionPassengers)
  const [countFares] = useAtom(bookingSessionCountFares)
  const [filteredFares, setFilteredFares] = useState(
    filterFares(toUpdateFareList, countFares, selected),
  )
  const [timeoutDeadline, setTimeoutDeadline] = useAtom(
    bookingSessionTimeoutDeadline,
  )
  const [settings] = useAtom(settingsConfig)

  const {
    trips,
    tripPages,
    localLoading,
    passengers,
    globalTrips,
    setLocalLoading,
    notFound,
    searchForTrips,
    setTrips,
    organizeTripsIntoPages,
  } = useTripData()

  const backReturn = useRef(false)
  const corruptionHandledRef = useRef(false)

  // Handlers
  const handleExit = () => {
    navigate(`${BASE_URL}/`)
  }

  const handleSelectTrip = (trip) => {
    loggerDebug("Seleccionando viaje:", trip)
    setLocalLoading(true)

    if (isSingleTrip(bookingTripConfig)) {
      console.log("Seleccionando viaje de ida en sencillo: ", trip)
      setBookingDepartureTrip(trip)
      setShouldRedirectToBus(true)
    } else if (
      isSelectingRoundTripDeparture(
        bookingTripConfig,
        bookingDepartureTrip,
        filteredFares,
      )
    ) {
      //Verdadero si es redondo y se están seleccionando los asientos de regreso.
      console.log("Seleccionando viaje de ida en redondo: ", trip)
      setBookingDepartureTrip(trip)
      setShouldRedirectToBus(true)
    } else if (
      isSelectingRoundTripReturn(
        bookingTripConfig,
        bookingDepartureTrip,
        bookingReturningTrip,
        passengers,
      )
    ) {
      //Verdadero si es redondo y se están seleccionando los asientos de ida.
      console.log("Seleccionando viaje de regreso: ", trip)
      setBookingReturningTrip(trip)
      setShouldRedirectToBus(true)
    }

    updateFareList(trip.fareClasses[0].fares)
    setLocalLoading(false)
  }

  /**
   * Actualiza la lista de tarifas con los precios del viaje seleccionado.
   * @param {Array} faresTrip - Tarifas del viaje seleccionado.
   */
  const updateFareList = (faresTrip) => {
    setToUpdateFareList((prevState) =>
      prevState.map((item) => {
        const match = faresTrip.find((fare) => fare.id === item._id)

        if (match) {
          return {
            ...item,
            displayCurrencyValue: match.displayCurrencyValueToDisplay,
          }
        }

        return item
      }),
    )
  }

  //Revisa que trips cargar ya sea sencillo o redondo
  const handleGetTrips = async () => {
    setLocalLoading(true)

    try {
      const isRound = bookingTripConfig.type === TripType.round
      if (isRound && !timeoutDeadline) {
        console.log("entro if de viaje redondo")
        startBookingTimer({
          settings,
          setupTimer,
          showTimer,
          setDeadline: setTimeoutDeadline,
          onTimeout,
          source: "round-trips",
        })
      }
      if (!isSingleTrip(bookingTripConfig) && !isEmpty(passengers)) {
        console.log("Recuperando viajes redondos...", globalTrips.returns)
        setTrips(globalTrips.returns)
      } else {
        console.log("Buscando viajes sencillos...")
        await searchForTrips(
          bookingOrigin,
          bookingDestination,
          bookingDepartureDate,
          bookingReturningDate,
          bookingTripConfig.type === TripType.round,
          bookingFares,
        )
      }
    } catch (error) {
      console.error("Error al obtener viajes:", error)
    } finally {
      setLocalLoading(false)
    }
  }

  const handleBack = () => {
    if (isSingleTrip(bookingTripConfig)) {
      console.log("Seleccionando viaje de ida en sencillo: ")
      navigate(`${BASE_URL}/booking/search`)
    } else if (
      isSelectingRoundTripDeparture(
        bookingTripConfig,
        bookingDepartureTrip,
        filteredFares,
      )
    ) {
      //Verdadero si es redondo y se están seleccionando los asientos de regreso.
      console.log("Seleccionando viaje de ida en redondo: ")
      navigate(`${BASE_URL}/booking/search`)
    } else if (
      isSelectingRoundTripReturn(
        bookingTripConfig,
        bookingDepartureTrip,
        bookingReturningTrip,
        passengers,
      )
    ) {
      //Verdadero si es redondo y se están seleccionando los asientos de ida.
      console.log("Retornando a seleccion de viajes de ida: ")
      navigate(`${BASE_URL}/booking/bus`)
    }
  }

  // Efectos
  useEffect(() => {
    if (bookingOrigin && bookingDestination) {
      handleGetTrips()
    }
  }, [bookingOrigin, bookingDestination])

  // Detectar corrupción de estado clave y redirigir a search para reiniciar flujo.
  useEffect(() => {
    if (corruptionHandledRef.current) return

    // Reglas de corrupción:
    // 1. Origen/Destino faltan o no tienen _id.
    const originCorrupt =
      !bookingOrigin || typeof bookingOrigin !== "object" || !bookingOrigin._id
    const destinationCorrupt =
      !bookingDestination ||
      typeof bookingDestination !== "object" ||
      !bookingDestination._id
    // 2. Config de viaje ausente o tipo inválido.
    const tripConfigCorrupt =
      !bookingTripConfig ||
      (bookingTripConfig.type !== TripType.single &&
        bookingTripConfig.type !== TripType.round)
    // 3. Fecha de salida inválida.
    const departureDateCorrupt =
      !bookingDepartureDate || bookingDepartureDate.length < 8
    // 4. Si es redondo, fecha de regreso inválida.
    const returningDateCorrupt =
      bookingTripConfig?.type === TripType.round &&
      (!bookingReturningDate || bookingReturningDate.length < 8)
    // 5. Sesión global de viajes sin estructura esperada (cuando ya existe globalTrips).
    // Ajuste mínimo: no marcar corrupción mientras globalTrips sea un arreglo inicial ([])
    const globalTripsCorrupt =
      globalTrips &&
      !Array.isArray(globalTrips) &&
      (!globalTrips.departures ||
        !Array.isArray(globalTrips.departures) ||
        (bookingTripConfig?.type === TripType.round &&
          (!globalTrips.returns || !Array.isArray(globalTrips.returns))))

    const isCorrupt =
      originCorrupt ||
      destinationCorrupt ||
      tripConfigCorrupt ||
      departureDateCorrupt ||
      returningDateCorrupt ||
      globalTripsCorrupt

    if (isCorrupt) {
      corruptionHandledRef.current = true
      // Observabilidad: registrar evento en Sentry y consola antes de redirigir
      try {
        Sentry.captureMessage("BookingTrips state corrupt", {
          level: "error",
          extra: {
            originCorrupt,
            destinationCorrupt,
            tripConfigCorrupt,
            departureDateCorrupt,
            returningDateCorrupt,
            globalTripsCorrupt,
            bookingTripType: bookingTripConfig?.type,
          },
        })
      } catch (e) {
        // evitar que una falla en sentry bloquee la recuperación
        console.error("[BookingTrips] Error sending Sentry corruption event", e)
      }
      console.error("[BookingTrips] State corrupt -> redirect to search", {
        originCorrupt,
        destinationCorrupt,
        tripConfigCorrupt,
        departureDateCorrupt,
        returningDateCorrupt,
        globalTripsCorrupt,
      })
      // Limpieza mínima. No mostramos modal aquí; se mostrará en `BookingSearch`.
      navigate(`${BASE_URL}/booking/search`, {
        state: { problem: "trips-corrupt" },
        replace: true,
      })
    }
  }, [
    bookingOrigin,
    bookingDestination,
    bookingTripConfig,
    bookingDepartureDate,
    bookingReturningDate,
    globalTrips,
    navigate,
  ])

  useEffect(() => {
    if (Array.isArray(trips) && trips.length > 0) {
      organizeTripsIntoPages(trips)
    }
  }, [trips])

  useEffect(() => {
    if (shouldRedirectToBus) {
      console.log("Redirigiendo a la página de selección de asientos... (bus)")
      navigate(`${BASE_URL}/booking/bus`)
      setShouldRedirectToBus(false)
    }
  }, [shouldRedirectToBus])

  const onTimeout = () => {
    handleBookingTimeout({
      hideTimer,
      setDeadline: setTimeoutDeadline,
      setupModal,
      showModal,
      navigate,
      hideModal,
      message:
        "¡Lo sentimos! 😓 El tiempo para continuar con tu compra terminó. Vuelve a iniciar tu proceso cuando estés listo.",
    })
  }

  useEffect(() => {
    if (timeoutDeadline) {
      const diff = differenceInSeconds(parseISO(timeoutDeadline), new Date())
      if (diff <= 0) {
        onTimeout()
      }
    }
  }, [timeoutDeadline])

  return (
    <>
      <Loader show={localLoading} />
      <section className={["section-full", "section-flex"].join(" ")}>
        <div className={[global.top, styles.top].join(" ")}>
          <div
            className={[
              "logo",
              bookingTripConfig.redondo ? "roundAlign" : null,
              styles.logo,
            ].join(" ")}
          >
            <figure
              className={[
                "logo-image",
                "logo-image-transpais-light",
                bookingTripConfig.redondo ? "logo-small" : null,
              ].join(" ")}
            />
            {!isSingleTrip(bookingTripConfig) && (
              <div className={styles.toPill}>
                <div className={styles.pill}>
                  <p>
                    {isEmpty(passengers)
                      ? "Selecciona tu viaje de ida"
                      : "Selecciona tu viaje de regreso"}
                  </p>
                </div>
              </div>
            )}
            <Timer variant={"clock"} />
          </div>

          <div className={styles.middle}>
            <div className={global.content}>
              <div className={styles.tripsHeader}>
                {/* <TripData variant={'brief'} data={{
                  origin: bookingOrigin,
                  destination: bookingDestination,
                  returning: bookingTripConfig.redondo,
                }} /> */}
                {/* <TripData variant={'meta'} meta={{ departure: bookingDepartureTrip, returning: bookingReturningTrip }} /> */}
              </div>

              <hr />

              {tripPages.length > 0 ? (
                <div className={styles.departures}>
                  <Carrousel width={1856}>
                    <div className={styles.pages}>
                      {tripPages.map((page, i) => (
                        <div key={`tp_${i}`} className={styles.departuresPage}>
                          {page.map((trip) => (
                            <Departure
                              key={`tpd_${trip.id}`}
                              onClick={() => handleSelectTrip(trip)}
                              variant={DepartureViewVariant.full}
                              data={trip}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </Carrousel>
                </div>
              ) : notFound ? (
                <div className={styles.departuresEmpty}>
                  <div className={styles.instructions}>
                    <div
                      className={styles.retry}
                      onClick={() =>
                        searchForTrips(
                          bookingOrigin,
                          bookingDestination,
                          bookingDepartureDate,
                          bookingReturningDate,
                          bookingTripConfig.type === TripType.round,
                        )
                      }
                    >
                      Reintentar
                    </div>
                    <h4>
                      ¡Ops! No encontré viajes a tu destino. Puedes reintentar
                      la búsqueda o cambiar la fecha.
                    </h4>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <footer className={global.bottom}>
          <div>
            <Button
              onClick={handleBack}
              type="neutral"
              left-icon="chevron-left"
            >
              Volver
            </Button>
          </div>
          <div className={global.middle}>
            <Button onClick={handleExit} type="positive" right-icon="house">
              Ir a inicio
            </Button>
          </div>
          <div>
            {/* <Button to={`${BASE_URL}/booking/bus`} type="positive" right-icon="chevron-right">Continuar</Button> */}
          </div>
        </footer>
      </section>
    </>
  )
}

export default BookingTrips
